import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGuideNotes, useSaveGuideNote } from "@/hooks/api/guides";
import { apiErrorMessage } from "@/lib/api";
import { Linkify } from "@/lib/linkify";

/**
 * One Staff member's own private "Notes & Links" on a guide entry —
 * visible only to them, never to other Staff or Admin (see
 * StaffGuideNote). Whatever they type — a pasted URL, a reminder, both —
 * with any http(s) URL in the saved text rendered clickable via Linkify.
 * Unlike PersonalizedSpiel (FlowPlayerPage), there's no "reset to
 * original" concept here — blank just means no note, so Save with an
 * empty box clears it.
 *
 * Fetches all of the current user's notes in one bulk request the first
 * time any card expands (see useGuideNotes) — cheap to mount one of these
 * per expanded card since react-query dedupes the underlying query.
 * `compact` tightens sizing for the sidebar GuidePanel; the full
 * GuideListPage uses the default.
 */
export function GuideNotesEditor({ entryId, compact = false }: { entryId: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { data: notesMap, isLoading: notesLoading } = useGuideNotes();
  const saveNote = useSaveGuideNote();
  const savedNote = notesMap?.[entryId] ?? "";

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(savedNote);

  // Keep the draft in sync with the saved value while not editing (e.g.
  // once the bulk fetch above resolves after this card was already open)
  // — but never clobber what someone's actively typing.
  useEffect(() => {
    if (!editing) setDraft(savedNote);
  }, [savedNote, editing]);

  const startEditing = () => {
    // Guards against the race the button's `disabled` below mainly
    // covers: without this, clicking in the instant before the bulk
    // fetch resolves would seed the draft from a still-default "" (read
    // as "no note yet") and a Save would silently overwrite whatever
    // note actually exists server-side.
    if (notesLoading) return;
    setDraft(savedNote);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(savedNote);
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      await saveNote.mutateAsync({ entryId, note: draft });
      setEditing(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, t("guideList.notesSaveError")));
    }
  };

  const textSize = compact ? "text-xs" : "text-sm";

  return (
    <div className={`rounded-md border border-slate-200 bg-surface-alt ${compact ? "p-2.5" : "p-3"} ${textSize}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-slate-700">{t("guideList.notesHeading")}</span>
        {!editing && (
          <button
            type="button"
            onClick={startEditing}
            disabled={notesLoading}
            className="flex shrink-0 items-center gap-1 text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline [-webkit-tap-highlight-color:transparent]"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
            {savedNote ? t("guideList.notesEdit") : t("guideList.notesAdd")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            autoFocus
            rows={compact ? 3 : 4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("guideList.notesPlaceholder")}
            className={textSize}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={handleCancel}>
              {t("guideList.notesCancel")}
            </Button>
            <Button type="button" size="sm" onClick={handleSave} loading={saveNote.isPending}>
              {t("guideList.notesSave")}
            </Button>
          </div>
        </div>
      ) : notesLoading ? (
        // Distinct from the "nothing here yet" empty state below — this
        // means "don't know yet", not "confirmed empty". See the same
        // distinction guarding startEditing/the button above.
        <p className="mt-1.5 text-slate-400">{t("common.loading")}</p>
      ) : savedNote ? (
        <Linkify text={savedNote} className="mt-1.5 whitespace-pre-wrap text-slate-700" />
      ) : (
        <p className="mt-1.5 text-slate-400">{t("guideList.notesEmpty")}</p>
      )}
    </div>
  );
}
